import { CompletionSource } from '@codemirror/autocomplete';
import { EditorState, EditorStateConfig, Extension, StateField } from '@codemirror/state';
import { EditorView, ViewUpdate } from '@codemirror/view';
import { forwardRef, HTMLAttributes, useImperativeHandle, useRef } from 'react';

import { SqlSchema } from 'dashboard/components-lib/CodeEditor/completionSources/sql/personaSqlDatasource';
import { useCodeMirror } from 'dashboard/components-lib/CodeEditor/useCodeMirror';

// This component has been heavily inspired by the react-codemirror package
// https://github.com/uiwjs/react-codemirror/blob/master/core/src/index.tsx
export interface CodeMirrorEditorProps
  extends Omit<EditorStateConfig, 'doc' | 'extensions'>,
    Omit<HTMLAttributes<HTMLDivElement>, 'onChange' | 'placeholder'> {
  /** focus on the editor. */
  autoFocus?: boolean;

  code: string;
  /**
   * Completion Sources can be [provided](https://codemirror.net/docs/ref/#autocomplete.CompletionSource) when creating a state to build a custom intellisense behavior.
   * This can be a built-in language specific completion source, a custom completion source designed around a custom schema, or any other autocompletion behavior you desire.
   * Note that as of the first iteration of this tool, passing in a completion source overides the [localCompletionSource](https://github.com/codemirror/lang-javascript#user-content-localcompletionsource).
   **/
  completionSources?: {
    javascript?: CompletionSource[];
    sql?: CompletionSource[];
  };
  /**
   * This disables editing of the editor content by the user.
   * @default true
   */
  editable?: boolean;
  /**
   * Extension values can be [provided](https://codemirror.net/6/docs/ref/#state.EditorStateConfig.extensions) when creating a state to attach various kinds of configuration and behavior information.
   * They can either be built-in extension-providing objects,
   * such as [state fields](https://codemirror.net/6/docs/ref/#state.StateField) or [facet providers](https://codemirror.net/6/docs/ref/#state.Facet.of),
   * or objects with an extension in its `extension` property. Extensions can be nested in arrays arbitrarily deep—they will be flattened when processed.
   */
  extensions?: Extension[];
  hasError?: boolean;
  /**
   * Create a state from its JSON representation serialized with [toJSON](https://codemirror.net/docs/ref/#state.EditorState.toJSON) function
   */
  initialState?: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    json: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fields?: Record<string, StateField<any>>;
  };
  /**
   * The active language for the editor. Examples: 'javascript', 'sql'
   */
  language: string;

  /** Fired whenever a change occurs to the document. */
  onChange?(value: string, viewUpdate: ViewUpdate): void;

  /** Fired whenever the user presses the Command+Enter. */
  onCommandEnter?: () => void;

  /** The first time the editor executes the event. */
  onCreateEditor?(view: EditorView, state: EditorState): void;

  /** Fired whenever any state change occurs within the editor, including non-document changes like lint results. */
  onUpdate?(viewUpdate: ViewUpdate): void;

  /** Enables a placeholder—a piece of example content to show when the editor is empty. */
  placeholder?: string | HTMLElement;
  /**
   * This disables editing of the editor content by the user.
   * @default false
   */
  readOnly?: boolean;
  /**
   * If the view is going to be mounted in a shadow root or document other than the one held by the global variable document (the default), you should pass it here.
   * Originally from the [config of EditorView](https://codemirror.net/6/docs/ref/#view.EditorView.constructor%5Econfig.root)
   */
  root?: ShadowRoot | Document;
  /**
   * The schema to use for autocompletion to create a context for the user to work with.
   */
  schemas?: {
    sql?: SqlSchema;
  };
  /** value of the auto created model in the editor. */
  value?: string;

  /** disable built-in debounce logic to manage debouncing in caller's end */
  disableDebounce?: boolean;
}

export interface CodeMirrorEditorRef {
  editor?: HTMLDivElement | null;
  state?: EditorState;
  view?: EditorView;
}

const CodeEditor = forwardRef<CodeMirrorEditorRef, CodeMirrorEditorProps>((props, ref) => {
  const {
    autoFocus,
    className,
    code,
    completionSources,
    disableDebounce,
    initialState,
    editable,
    extensions = [],
    hasError,
    language,
    onChange,
    onCreateEditor,
    onCommandEnter,
    onUpdate,
    placeholder,
    readOnly,
    root,
    schemas,
    selection,
    value = '',
    ...other
  } = props;

  const editor = useRef<HTMLDivElement>(null);
  const { state, view, container } = useCodeMirror({
    autoFocus,
    code,
    completionSources,
    container: editor.current,
    disableDebounce,
    editable,
    extensions,
    initialState,
    hasError,
    language,
    onChange,
    onCreateEditor,
    onCommandEnter,
    onUpdate,
    placeholder,
    readOnly,
    root,
    selection,
    schemas,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useImperativeHandle(ref, () => ({ editor: editor.current, state: state, view: view }), [
    container,
    editor,
    state,
    view,
  ]);

  // check type of value
  if (typeof value !== 'string') {
    throw new Error(`value must be typeof string but got ${typeof value}`);
  }

  return (
    <div
      ref={editor}
      className='cm-theme'
      style={{
        height: 'inherit',
        width: 'inherit',
      }}
      {...other}
    ></div>
  );
});

CodeEditor.displayName = 'CodeMirrorEditor';

export default CodeEditor;
